import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Bell,
  BellOff,
  Smartphone,
  AlertCircle,
  CheckCircle,
  Send,
  Monitor
} from "lucide-react";
import { usePushNotifications } from "../hooks/usePushNotifications";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PushNotificationSettings = () => {
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    isLoading,
    subscribe, 
    unsubscribe 
  } = usePushNotifications();
  const [isToggling, setIsToggling] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [deviceCount, setDeviceCount] = useState(0);

  useEffect(() => {
    if (isSubscribed) {
      fetchDeviceCount();
    }
  }, [isSubscribed]);

  const fetchDeviceCount = async () => {
    try {
      const response = await axios.get(`${API}/push/status`);
      setDeviceCount(response.data.device_count || 0);
    } catch (error) {
      console.error("Failed to fetch device count:", error);
    }
  };

  const handleToggle = async (enabled) => {
    setIsToggling(true);
    try {
      if (enabled) {
        await subscribe();
        toast.success("Push notifications enabled! You'll be notified when veterans need help.");
        fetchDeviceCount();
      } else {
        await unsubscribe();
        toast.success("Push notifications disabled.");
        setDeviceCount(0);
      }
    } catch (error) {
      if (error.message === 'Notification permission denied') {
        toast.error("Please allow notifications in your browser settings.");
      } else {
        toast.error("Failed to update notification settings.");
      }
    } finally {
      setIsToggling(false);
    }
  };

  const handleSendTest = async () => {
    setIsSendingTest(true);
    try {
      const response = await axios.post(`${API}/push/test`);
      toast.success(response.data.message || "Test notification sent!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send test notification";
      toast.error(message);
    } finally {
      setIsSendingTest(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="card-valor">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="text-muted-foreground">Loading notification settings...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!isSupported) {
    return (
      <Card className="card-valor">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0" />
            <div>
              <p className="font-semibold text-foreground">Push Notifications Not Supported</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your browser doesn't support push notifications. Try using Chrome, Firefox, or Edge.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-valor">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <Bell className="w-5 h-5 text-primary" />
          Browser Notifications
        </CardTitle>
        <CardDescription>
          Get instant alerts when veterans need support
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isSubscribed ? "bg-green-100" : "bg-muted"
            }`}>
              {isSubscribed ? (
                <Bell className="w-6 h-6 text-green-600" />
              ) : (
                <BellOff className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Label className="font-semibold">Push Notifications</Label>
                {isSubscribed && (
                  <Badge className="bg-green-600 text-xs">Active</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {isSubscribed 
                  ? "You'll receive alerts even when the app is closed"
                  : "Enable to receive real-time alerts"}
              </p>
            </div>
          </div>
          <Switch
            checked={isSubscribed}
            onCheckedChange={handleToggle}
            disabled={isToggling}
            data-testid="push-notifications-toggle"
          />
        </div>

        {permission === 'denied' && (
          <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-100">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800">Notifications Blocked</p>
              <p className="text-sm text-red-700 mt-1">
                You've blocked notifications for this site. To enable them:
              </p>
              <ol className="text-sm text-red-700 mt-2 list-decimal list-inside space-y-1">
                <li>Click the lock icon in your browser's address bar</li>
                <li>Find "Notifications" settings</li>
                <li>Change from "Block" to "Allow"</li>
                <li>Refresh this page</li>
              </ol>
            </div>
          </div>
        )}

        {isSubscribed && (
          <>
            <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-100">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-800">Notifications Enabled</p>
                <p className="text-sm text-green-700 mt-1">
                  You'll receive instant browser alerts when:
                </p>
                <ul className="text-sm text-green-700 mt-2 list-disc list-inside space-y-1">
                  <li>A veteran requests crisis support</li>
                  <li>Someone uses Quick Help</li>
                  <li>You have upcoming appointments</li>
                </ul>
              </div>
            </div>

            {/* Multi-device info and test button */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-3">
                <Monitor className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">
                    {deviceCount} device{deviceCount !== 1 ? 's' : ''} connected
                  </p>
                  <p className="text-sm text-blue-700">
                    Notifications work on multiple devices
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendTest}
                disabled={isSendingTest}
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
                data-testid="send-test-notification-btn"
              >
                {isSendingTest ? (
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Test
              </Button>
            </div>
          </>
        )}

        <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
          <Smartphone className="w-4 h-4" />
          <span>Works on desktop and mobile browsers</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default PushNotificationSettings;
